import React, { createContext, useContext, ReactNode, useState } from 'react'
import { useAuth } from '../auth-context/AuthContext'
import {
  apiResponse,
  createDownloadLink,
  ResponseType,
  sanitizeFilename,
  showSnackbar,
} from '../../utils/Utils'
import { useFetchWithAuth } from '../../hooks/useFetchWithAuth'
import { findFileNameById } from '../../utils/DocumentUtils'
import { DocumentRoot } from '../../lib/DocumentLib'
import { VariantType } from '../../lib/CommonLib'

interface DocumentViewContextType {
  roots: DocumentRoot[]
  documentList: any
  path: any
  setPath: (val: any) => void
  selectedRoot: string
  setSelectedRoot: (val: string) => void
  downLoadDocument: (val: string) => void
  deleteDocument: (fileId: string) => void
  goBack: () => void
  goUp: () => void
  canGoBack: boolean
  canGoUp: boolean
  fetchDocumentList: () => void
  uploadDocument: (file: any, newPath: string, fileName: string, tags: string) => void
  renameDocument: (fileId: any, newFileName: string) => void
  approveDocument: (fileId: string) => void
  rejectDocument: (fileId: string, reason: string) => void
  fetchRoots: () => void
}

const DocumentContext = createContext<DocumentViewContextType | undefined>(undefined)

export const DocumentViewProvider = ({ children }: { children: ReactNode }) => {
  const { user, setSnackbarOpen, setSnackbarMessage, setVariant, setLoading } = useAuth()
  const fetchWithAuth = useFetchWithAuth()
  const [documentList, setDocumentList] = useState<any>([])
  const [roots, setRoots] = useState<DocumentRoot[]>([])
  const [selectedRoot, setSelectedRoot] = useState<string>('')
  const [path, setPath] = useState<any>([])
  const [pathHistory, setPathHistory] = useState<any>([])
  const canGoBack = pathHistory.length > 0
  const canGoUp = path.length > 1

  const fetchRoots = async () => {
    try {
      setLoading(true)
      const response = await fetchWithAuth('/api/Document/Roots', {
        headers: {
          'Content-Type': 'application/json',
        },
      })
      const data = await apiResponse(response)
      setRoots(data)
      setSelectedRoot(data[0]?.path)
      setPath([data[0]?.path])
    } catch (error) {
      showSnackbar(`${error}`, VariantType.error, setVariant, setSnackbarOpen, setSnackbarMessage)
      setRoots([])
      setSelectedRoot('')
      setPath([])
    } finally {
      setLoading(false)
    }
  }

  const fetchDocumentList = async () => {
    try {
      setLoading(true)
      const basePath = '/api/Document/Tree'
      const pathString = `?path=${path.join('%2F')}`
      const response = await fetchWithAuth(`${basePath}${pathString}`, {
        headers: {
          'Content-Type': 'application/json',
        },
      })
      const data = await apiResponse(response)
      setDocumentList(data)
    } catch (error) {
      showSnackbar(`${error}`, VariantType.error, setVariant, setSnackbarOpen, setSnackbarMessage)
    } finally {
      setLoading(false)
    }
  }

  const downLoadDocument = async (fileId: string) => {
    try {
      setLoading(true)
      const response = await fetchWithAuth(`/api/Document/Download?id=${fileId}`, {
        headers: {
          'Content-Type': 'application/json',
        },
      })
      if (!response.ok) {
        throw new Error('Failed to download document')
      }
      let filename = findFileNameById(documentList, fileId)
      if (!filename) {
        filename = 'downloaded_file'
        return
      }
      filename = sanitizeFilename(filename)
      const blob = await apiResponse(response, ResponseType.blob)
      const downloadUrl = window.URL.createObjectURL(blob)
      createDownloadLink(downloadUrl, filename)
      window.URL.revokeObjectURL(downloadUrl)
      showSnackbar(
        'Document successfully downloaded',
        VariantType.success,
        setVariant,
        setSnackbarOpen,
        setSnackbarMessage
      )
    } catch (error) {
      showSnackbar(`${error}`, VariantType.error, setVariant, setSnackbarOpen, setSnackbarMessage)
    } finally {
      setLoading(false)
    }
  }

  const deleteDocument = async (fileId: string) => {
    try {
      setLoading(true)
      const response = await fetchWithAuth(`/api/Document?id=${fileId}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
      })
      await apiResponse(response, ResponseType.text)
      showSnackbar(
        'Document successfully deleted',
        VariantType.success,
        setVariant,
        setSnackbarOpen,
        setSnackbarMessage
      )
      fetchDocumentList()
    } catch (error) {
      showSnackbar(`${error}`, VariantType.error, setVariant, setSnackbarOpen, setSnackbarMessage)
    } finally {
      setLoading(false)
    }
  }

  const setNewPath = (newPath: any) => {
    setPathHistory((prev: any) => [...prev, path])
    setPath(newPath)
  }

  const goBack = () => {
    if (pathHistory.length > 0) {
      const previousPath = pathHistory[pathHistory.length - 1]
      setPath(previousPath)
      setPathHistory((prev: string | any[]) => prev.slice(0, -1))
    }
  }

  const goUp = () => {
    if (path.length > 1) {
      const newPath = path.slice(0, -1)
      setNewPath(newPath)
    }
  }

  const uploadDocument = async (file: any, newPath: string, fileName: string, tags: string) => {
    try {
      setLoading(true)
      const formData = new FormData()
      formData.append('file', file)
      formData.append('FileName', fileName)
      const relativePath = newPath.startsWith(path.join('/'))
        ? newPath
        : `${path.join('/')}/${newPath}`
      formData.append('RelativePath', relativePath)
      formData.append('UploadedUser', user.initial)
      formData.append('Tags', tags)

      const response = await fetchWithAuth('/api/Document/Upload', {
        method: 'POST',
        body: formData,
      })
      await apiResponse(response)
      showSnackbar(
        `Document successfully uploaded`,
        VariantType.success,
        setVariant,
        setSnackbarOpen,
        setSnackbarMessage
      )
      fetchDocumentList()
    } catch (error) {
      showSnackbar(`${error}`, VariantType.error, setVariant, setSnackbarOpen, setSnackbarMessage)
    } finally {
      setLoading(false)
    }
  }

  const renameDocument = async (fileId: any, newFileName: string) => {
    try {
      setLoading(true)
      const formData = new FormData()
      formData.append('FileId', fileId)
      formData.append('NewFileName', newFileName)
      formData.append('RelativePath', path.join('/'))
      formData.append('UpdatedUser', user.initial)

      const response = await fetchWithAuth(`/api/Document/Rename`, {
        method: 'POST',
        body: formData,
      })
      await apiResponse(response, ResponseType.text)
      showSnackbar(
        'Document successfully renamed',
        VariantType.success,
        setVariant,
        setSnackbarOpen,
        setSnackbarMessage
      )
      fetchDocumentList()
    } catch (error) {
      showSnackbar(`${error}`, VariantType.error, setVariant, setSnackbarOpen, setSnackbarMessage)
    } finally {
      setLoading(false)
    }
  }

  const approveDocument = async (fileId: string) => {
    try {
      setLoading(true)
      const response = await fetchWithAuth(`/api/Upload/Approve?uploadId=${fileId}`, {
        method: 'POST',
      })
      await apiResponse(response, ResponseType.text)
      showSnackbar(
        'Document successfully approve',
        VariantType.success,
        setVariant,
        setSnackbarOpen,
        setSnackbarMessage
      )
      fetchDocumentList()
    } catch (error) {
      showSnackbar(`${error}`, VariantType.error, setVariant, setSnackbarOpen, setSnackbarMessage)
    } finally {
      setLoading(false)
    }
  }

  const rejectDocument = async (fileId: string, reason: string) => {
    try {
      setLoading(true)
      const response = await fetchWithAuth(
        `/api/Upload/Reject?uploadId=${fileId}&reason=${reason}`,
        {
          method: 'POST',
        }
      )
      await apiResponse(response, ResponseType.text)
      showSnackbar(
        'Document successfully reject',
        VariantType.success,
        setVariant,
        setSnackbarOpen,
        setSnackbarMessage
      )
      fetchDocumentList()
    } catch (error) {
      showSnackbar(`${error}`, VariantType.error, setVariant, setSnackbarOpen, setSnackbarMessage)
    } finally {
      setLoading(false)
    }
  }

  return (
    <DocumentContext.Provider
      value={{
        roots,
        documentList,
        path,
        setPath,
        selectedRoot,
        setSelectedRoot,
        downLoadDocument,
        deleteDocument,
        goBack,
        goUp,
        canGoBack,
        canGoUp,
        fetchDocumentList,
        uploadDocument,
        renameDocument,
        approveDocument,
        rejectDocument,
        fetchRoots,
      }}
    >
      {children}
    </DocumentContext.Provider>
  )
}

export const useDocumentView = () => {
  const context = useContext(DocumentContext)
  if (context === undefined) {
    throw new Error('useDocumentView must be used within a DocumentViewProvider')
  }
  return context
}
